import React, { type SetStateAction, useState } from "react"

import { useTranslation } from "react-i18next"

import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined"
import AddOutlinedIcon from "@mui/icons-material/AddOutlined"
import { Grid } from "@mui/material"
import { Button, Menu, MenuItem, Select, Typography } from "@synapse-analytics/synapse-ui"
import { FieldArray, useFormikContext } from "formik"
import { v4 as uuidv4 } from "uuid"

import { getTheme } from "../../../hooks/UseTheme"
import type { TagCardFormikValues } from "../../Taglist"
import {
  createEmptyCondition,
  getAddConditionFieldPath,
  getAddGroupFieldPath,
  getConditionAndOrFieldPath,
  getConditionId,
  getFieldArrayName,
  getGroupAndOrFieldPath,
  handleRemoveCondition,
} from "../helpers"
import type { GroupConditions, GroupRendererProps, RuleCardFormikValues } from "../interfaces"
import { Condition } from "./Condition"
import { DataBlock } from "./RuleCard"

import styles from "./RuleCard.module.scss"

/**
 * Recursive component for rendering nested groups
 */
export function GroupRenderer(props: GroupRendererProps): React.ReactElement {
  // Destructure props
  const { groups, ruleIndex, parentPath, editMode, createMode, currentDepth, maxDepth, andOr, setValues } = props

  // hooks
  const { values, setFieldValue } = useFormikContext<RuleCardFormikValues>()
  const theme = getTheme()
  const { t } = useTranslation("rulesets")

  // states
  const [groupAnchors, setGroupAnchors] = useState<{ [key: string]: HTMLElement | null }>({})

  // constants
  const emptyCondition = createEmptyCondition()

  // helper functions
  const onAddCondition = (currentPath: number[], group: GroupConditions): void => {
    const fieldPath = getAddConditionFieldPath({ ruleIndex, currentPath })
    setFieldValue(fieldPath, [...(group.conditions || []), emptyCondition])
  }

  const onAddGroup = (currentPath: number[], group: GroupConditions): void => {
    const newGroup = {
      id: uuidv4(),
      andOr: "and",
      conditions: [emptyCondition as never, emptyCondition as never],
      groups: [],
    }
    const fieldPath = getAddGroupFieldPath({ ruleIndex, currentPath })
    setFieldValue(fieldPath, [...(group.groups || []), newGroup])
  }

  // early return
  if (currentDepth >= maxDepth) {
    return <></>
  }

  return (
    <Grid item xs>
      {groups.map((group, groupIndex) => {
        const currentPath = [...parentPath, groupIndex]
        const pathKey = currentPath.join("-")

        return (
          <Grid display="flex" flexDirection="row" gap={1} mb={1} key={`group-${ruleIndex}-${pathKey}-${group.id}`}>
            {/* And/Or selector for this group */}
            <Grid item xs={3} md={2} lg={1.25}>
              {editMode || createMode ? (
                <Select
                  type="text"
                  value={group.andOr}
                  handleChange={(e) => {
                    const fieldPath = getGroupAndOrFieldPath({ ruleIndex, currentPath, groupIndex })
                    setFieldValue(fieldPath, e.target.value)
                  }}
                  fullWidth
                  options={andOr}
                  hideDescription
                />
              ) : (
                <DataBlock value={group.andOr ?? "and"} />
              )}
            </Grid>

            {/* Conditions in this group */}
            <FieldArray name={getFieldArrayName({ ruleIndex, currentPath })}>
              {({ remove: levelRemove }) => (
                <Grid
                  container
                  item
                  justifyContent="flex-end"
                  alignItems="flex-start"
                  className={styles.groupBackground}
                >
                  <Grid container item spacing={1}>
                    {group.conditions?.map((condition, conditionIndex) => (
                      <Grid item xs={12} key={`condition-${ruleIndex}-${pathKey}-${conditionIndex}-${condition?.id}`}>
                        <Condition
                          path={{ groupsIdx: currentPath, conditionIdx: conditionIndex }}
                          ruleIndex={ruleIndex}
                          id={getConditionId({ ruleIndex, currentPath, conditionIndex })}
                          index={conditionIndex}
                          editMode={editMode}
                          createMode={createMode}
                          removeCondition={() => {
                            handleRemoveCondition({
                              values,
                              setFieldValue,
                              setValues: setValues as (
                                values: SetStateAction<RuleCardFormikValues | TagCardFormikValues>,
                              ) => void,
                              levelRemove,
                              ruleIndex,
                              path: {
                                groupsIdx: currentPath,
                                conditionIdx: conditionIndex,
                              },
                            })
                          }}
                          lastSwitchSetter={(op) => {
                            const fieldPath = getConditionAndOrFieldPath({ ruleIndex, currentPath, conditionIndex })
                            setFieldValue(fieldPath, op)
                          }}
                        />
                      </Grid>
                    ))}

                    {/* Recursive rendering of nested groups */}
                    {group.groups && group.groups.length > 0 && (
                      <GroupRenderer
                        groups={group.groups}
                        ruleIndex={ruleIndex}
                        parentPath={currentPath}
                        editMode={editMode}
                        createMode={createMode}
                        currentDepth={currentDepth + 1}
                        maxDepth={maxDepth}
                        andOr={andOr}
                        setValues={setValues}
                      />
                    )}

                    {/* Add rule/group menu for this level */}
                    {(editMode || createMode) && group.conditions && group.conditions.length > 0 && (
                      <Grid container>
                        <Grid item lg={12} mt={1} ml={1}>
                          <Button
                            size="regular"
                            onClick={(e) =>
                              setGroupAnchors((prev) => ({
                                ...prev,
                                [pathKey]: e?.currentTarget as HTMLElement,
                              }))
                            }
                            disabled={false}
                          >
                            + {t("addRuleBtn")}
                          </Button>
                          <Menu
                            key={`basic-menu-${pathKey}`}
                            anchorEl={groupAnchors[pathKey]}
                            open={Boolean(groupAnchors[pathKey])}
                            onClose={() => setGroupAnchors((prev) => ({ ...prev, [pathKey]: null }))}
                            menuMaxContent
                          >
                            <MenuItem
                              onClick={() => {
                                onAddCondition(currentPath, group)
                                setGroupAnchors((prev) => ({ ...prev, [pathKey]: null }))
                              }}
                            >
                              <Grid container display={"flex"} gap={1} height={"20px"}>
                                <AddOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                                <Typography variant="a">{t("addRuleBtn")}</Typography>
                              </Grid>
                            </MenuItem>
                            {currentDepth < maxDepth - 1 && (
                              <MenuItem
                                onClick={() => {
                                  onAddGroup(currentPath, group)
                                  setGroupAnchors((prev) => ({ ...prev, [pathKey]: null }))
                                }}
                              >
                                <Grid container display={"flex"} gap={1} height={"20px"}>
                                  <AddBoxOutlinedIcon fontSize="small" htmlColor={theme.palette.base.text[1]} />
                                  <Typography variant="a">{t("addGroupBtn")}</Typography>
                                </Grid>
                              </MenuItem>
                            )}
                          </Menu>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}
            </FieldArray>
          </Grid>
        )
      })}
    </Grid>
  )
}
